import type { SupabaseClient } from "@supabase/supabase-js";

export interface FeedProductOption {
  id: string;
  displayName: string;
  defaultUnit: string;
}

export interface FeedingRunView {
  id: string;
  roundNo: number;
  scheduledAt: string;
  targetDueAt: string;
  hardCutoffAt: string;
  status: "scheduled" | "open" | "closed" | "completed";
  totalTanks: number;
  completedTanks: number;
  missedTanks: number;
}

export interface FeedingQueueItemView {
  id: string;
  roundRunId: string;
  tankId: string;
  tankCode: string;
  tankFamily: string;
  queueOrder: number;
  status: "pending" | "completed" | "missed" | "not_applicable";
}

export interface FeedingSnapshot {
  farmId: string;
  timezoneName: string;
  localDate: string;
  capturedAt: string;
  products: FeedProductOption[];
  runs: FeedingRunView[];
  items: FeedingQueueItemView[];
}

export interface FeedingTarget {
  run: FeedingRunView;
  item: FeedingQueueItemView;
  mode: "current" | "late";
}

const CACHE_PREFIX = "discus.v2.feeding.snapshot:";

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function defaultStorage(): StorageLike | undefined {
  return typeof localStorage === "undefined" ? undefined : localStorage;
}

export function localDateInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const values = new Map(parts.map((part) => [part.type, part.value]));
  const year = values.get("year");
  const month = values.get("month");
  const day = values.get("day");

  if (!year || !month || !day) {
    throw new Error("FEEDING_LOCAL_DATE_FAILED");
  }

  return `${year}-${month}-${day}`;
}

export function currentFeedingTarget(
  snapshot: FeedingSnapshot,
  observedAt: Date,
): FeedingTarget | undefined {
  const now = observedAt.getTime();
  const run = snapshot.runs
    .filter((candidate) => {
      const start = Date.parse(candidate.scheduledAt);
      const cutoff = Date.parse(candidate.hardCutoffAt);
      return now >= start && now < cutoff;
    })
    .sort((left, right) => Date.parse(right.scheduledAt) - Date.parse(left.scheduledAt))[0];

  if (!run) return undefined;

  const item = snapshot.items
    .filter((candidate) => candidate.roundRunId === run.id && candidate.status === "pending")
    .sort((left, right) => left.queueOrder - right.queueOrder)[0];

  return item ? { run, item, mode: "current" } : undefined;
}

export function lateFeedingTargets(snapshot: FeedingSnapshot, observedAt: Date): FeedingTarget[] {
  const now = observedAt.getTime();
  const runById = new Map(snapshot.runs.map((run) => [run.id, run]));

  const targets: FeedingTarget[] = [];

  for (const item of snapshot.items) {
    const run = runById.get(item.roundRunId);
    if (!run) continue;

    const isLate =
      item.status === "missed" ||
      (item.status === "pending" && Date.parse(run.hardCutoffAt) <= now);

    if (isLate) {
      targets.push({ run, item, mode: "late" });
    }
  }

  return targets.sort(
    (left, right) =>
      left.run.roundNo - right.run.roundNo || left.item.queueOrder - right.item.queueOrder,
  );
}

export function writeFeedingSnapshotCache(
  snapshot: FeedingSnapshot,
  storage: StorageLike | undefined = defaultStorage(),
): void {
  storage?.setItem(`${CACHE_PREFIX}${snapshot.farmId}`, JSON.stringify(snapshot));
}

export function readFeedingSnapshotCache(
  farmId: string,
  storage: StorageLike | undefined = defaultStorage(),
): FeedingSnapshot | undefined {
  const raw = storage?.getItem(`${CACHE_PREFIX}${farmId}`);
  if (!raw) return undefined;

  try {
    const parsed = JSON.parse(raw) as FeedingSnapshot;
    return parsed?.farmId === farmId ? parsed : undefined;
  } catch {
    return undefined;
  }
}

export async function loadFeedingSnapshot(
  client: SupabaseClient,
  input: { farmId: string; observedAt?: Date },
): Promise<FeedingSnapshot> {
  const observedAt = input.observedAt ?? new Date();

  const farmResult = await client
    .from("farms")
    .select("id,timezone_name")
    .eq("id", input.farmId)
    .maybeSingle();

  if (farmResult.error) throw farmResult.error;
  if (!farmResult.data?.timezone_name) throw new Error("FEEDING_FARM_TIMEZONE_REQUIRED");

  const timezoneName = String(farmResult.data.timezone_name);
  const localDate = localDateInTimeZone(observedAt, timezoneName);

  const [productsResult, runsResult] = await Promise.all([
    client
      .from("feed_products")
      .select("id,display_name,default_unit,is_active")
      .eq("farm_id", input.farmId)
      .eq("is_active", true)
      .order("product_code"),
    client
      .from("feeding_round_runs")
      .select(
        "id,round_no,scheduled_at,target_due_at,hard_cutoff_at,status,total_tanks,completed_tanks,missed_tanks",
      )
      .eq("farm_id", input.farmId)
      .eq("local_date", localDate)
      .order("round_no"),
  ]);

  if (productsResult.error) throw productsResult.error;
  if (runsResult.error) throw runsResult.error;

  const runs: FeedingRunView[] = (runsResult.data ?? []).map((row) => ({
    id: String(row.id),
    roundNo: Number(row.round_no),
    scheduledAt: String(row.scheduled_at),
    targetDueAt: String(row.target_due_at),
    hardCutoffAt: String(row.hard_cutoff_at),
    status: String(row.status) as FeedingRunView["status"],
    totalTanks: Number(row.total_tanks),
    completedTanks: Number(row.completed_tanks),
    missedTanks: Number(row.missed_tanks),
  }));

  const runIds = runs.map((run) => run.id);
  const itemsResult = runIds.length
    ? await client
        .from("feeding_round_tanks")
        .select(
          "id,round_run_id,tank_id,tank_code_snapshot,tank_family_snapshot,queue_order,status",
        )
        .eq("farm_id", input.farmId)
        .in("round_run_id", runIds)
        .order("queue_order")
    : { data: [], error: null };

  if (itemsResult.error) throw itemsResult.error;

  return {
    farmId: input.farmId,
    timezoneName,
    localDate,
    capturedAt: observedAt.toISOString(),
    products: (productsResult.data ?? []).map((row) => ({
      id: String(row.id),
      displayName: String(row.display_name),
      defaultUnit: String(row.default_unit),
    })),
    runs,
    items: (itemsResult.data ?? []).map((row) => ({
      id: String(row.id),
      roundRunId: String(row.round_run_id),
      tankId: String(row.tank_id),
      tankCode: String(row.tank_code_snapshot),
      tankFamily: String(row.tank_family_snapshot),
      queueOrder: Number(row.queue_order),
      status: String(row.status) as FeedingQueueItemView["status"],
    })),
  };
}
